// ═══════════════════════════════════════════════════════════════
// Out of Cycle (OOC) - TypeScript Interfaces
// ═══════════════════════════════════════════════════════════════

import { SelectedPackSize } from 'Interfaces/FDPInterfaces/provisionalForecastV2Interfaces';

export type OOCStatus = 'draft' | 'communicated' | 'integrated';

export type OOCTabType = 'MAS' | 'NC' | 'ProvMAS' | 'ProvNC';

export type FCColorType = 'modified' | 'new' | 'removed' | 'unchanged' | 'ooc';

export type ProbabilityLevel = 'Low' | 'Medium' | 'High';

export type OOCReasonType =
  | 'New customer onboarding'
  | 'Customer confirmed as a loss'
  | 'Pack-out switch'
  | 'NDC changes - Phase-in/Phase-out'
  | 'NC wins/losses'
  | 'Discontinued'
  | 'Inability to compete with pricing'
  | 'Other TAA-compliant product in market'
  | 'Trigger for Provisional FC'
  | 'Delayed new product launch timelines'
  | 'Re-ramp'
  | 'Ramp to Base'
  | 'Market share changes'
  | 'Market size changes'
  | 'Pricing challenges'
  | 'Model'
  | 'Other';

export const OOC_REASONS: OOCReasonType[] = [
  'New customer onboarding',
  'Customer confirmed as a loss',
  'Pack-out switch',
  'NDC changes - Phase-in/Phase-out',
  'NC wins/losses',
  'Discontinued',
  'Inability to compete with pricing',
  'Other TAA-compliant product in market',
  'Trigger for Provisional FC',
  'Delayed new product launch timelines',
  'Re-ramp',
  'Ramp to Base',
  'Market share changes',
  'Market size changes',
  'Pricing challenges',
  'Model',
  'Other',
];

export const OOC_STATUS_OPTIONS: { label: string; value: OOCStatus | 'all' }[] = [
  { label: 'All Statuses', value: 'all' },
  { label: 'Draft', value: 'draft' },
  { label: 'Submitted / Communicated', value: 'communicated' },
  { label: 'Integrated', value: 'integrated' },
];

export const OOC_DATE_FILTER_OPTIONS = [
  'This Month',
  'Last 30 Days',
  'Last 90 Days',
  'All Time',
] as const;

export type OOCDateFilter = (typeof OOC_DATE_FILTER_OPTIONS)[number];

// ─── Monthly Forecast Cell ───────────────────────────────────
export interface OOCForecastCell {
  period: string; // ISO date "YYYY-MM-DD", day always 01 (e.g. "2026-05-01" = May 2026)
  value: number | null;
  previousValue: number | null;
  colorType: FCColorType;
}

// ─── Reference Data for FC calculation ───────────────────────
export interface OOCReferenceData {
  ndc11: string;
  twelveMonthSOAvg: number;
  sixMonthSOAvg: number;
  currentMonthPerformance: number;
  overallMarketDemand: number;
  newFC: number;
  percentChange: string;
}

// ─── Single NDC Forecast Adjustment (one row in the tab grid) ─
export interface OOCForecastAdjustment {
  id: string;
  oocId: string;
  ndc10: string;
  ndc11: string;
  supplier: string;
  molecule: string;
  concept: string;
  packOut: string;
  tab: OOCTabType;
  forecasts: OOCForecastCell[];
  notes: string;
  reason?: OOCReasonType; // per-row reason code
  currentMonthValue?: number | null; // read-only current month value (before/after reference)
  probability?: ProbabilityLevel; // only for ProvMAS tab
  referenceData?: OOCReferenceData;
  isFromRampingFile?: boolean;
  // Provisional-only fields (present when tab is ProvMAS or ProvNC)
  facilityRows?: OOCProvFacilityRow[];
  varianceRows?: OOCProvNCVarianceRow[];
  ncMonthPcts?: [number, number, number];
  ncThreshold?: number;
  // Per-pack volume breakdown + the operator's pack-size selection —
  // mirrors ProvisionalForecastV2's market_data / selected_packsizes so
  // toggling a pack recomputes annualVolSU / historical12MVolume the same
  // way the canonical Provisional Forecast screen does.
  marketData?: OOCMarketDataRow[];
  selectedPackSizes?: SelectedPackSize[];
}

// ─── OOC Submission ──────────────────────────────────────────
export interface OOCSubmission {
  id: string;
  reason?: OOCReasonType; // optional — reason is now captured per-row on each NDC
  submissionDate: string;
  submittedBy: string;
  status: OOCStatus;
  planningPeriod: string;
  contractTypes: OOCTabType[];
  ndcCount: number;
  adjustments: OOCForecastAdjustment[];
  communicatedDate?: string;
  integratedDate?: string;
  integratedIntoCycle?: string;
  emailPreview?: OOCEmailPreview;
}

// ─── Email Preview ───────────────────────────────────────────
export interface OOCEmailPreview {
  to: string;
  subject: string;
  attachment: string;
  bodyGreeting: string;
  bodySignature: string;
  masAdjustments: OOCForecastAdjustment[];
  ncAdjustments: OOCForecastAdjustment[];
  provMasAdjustments: OOCForecastAdjustment[];
  provNcAdjustments: OOCForecastAdjustment[];
}

// ─── Dashboard Summary Counts ────────────────────────────────
export interface OOCDashboardCounts {
  all: number;
  draft: number;
  communicated: number;
  scReviewing: number;
  history: number;
}

// ─── Audit Log ───────────────────────────────────────────────
export interface OOCAuditLogEntry {
  id: string;
  oocId: string;
  action: string;
  user: string;
  timestamp: string;
  details: string;
}

// ─── History Integration Card ────────────────────────────────
export interface OOCIntegrationSummary {
  oocId: string;
  date: string;
  submittedBy: string;
  status: OOCStatus;
  reason: string;
  contractTypes: OOCTabType[];
  ndcCount: number;
  integratedIntoCycle: string;
  searchText: string;
}

// ─── NDC Search Result ───────────────────────────────────────
export interface OOCNDCSearchResult {
  ndc10: string;
  ndc11: string;
  supplier: string;
  molecule: string;
  concept: string;
  packOut: string;
  contractType: 'MAS' | 'NC';
  planningType: 'Base' | 'Ramp' | 'Provisional';
  suggestedTab: OOCTabType;
}

// ─── Provisional Forecast Parameter Types ───────────────────
export interface OOCProvFacilityRow {
  facilityName: string;
  cot: string;
  rampGroup: string;
  isContingency: boolean;
  isSelected: boolean;
  annualVolSU: number;
  monthlyPercentages: number[]; // 15 values
  // Distinct AnnualMarketSales pack sizes on file for this facility, used to
  // compute SU (Selling Units) = sum of EU/packsize across every pack size.
  // Empty when no AnnualMarketSales pack-size data exists for the facility
  // (falls back to the NDC's own pack size in that case).
  packSizes?: number[];
  // Baseline monthlyPercentages before any user edits, and the per-month
  // color-coded edit status — mirrors OOCForecastCell.previousValue/colorType
  // so the Prov MAS grid gets the same new/modified/removed/unchanged
  // treatment as the normal MAS/NC forecast grids.
  originalMonthlyPercentages?: number[];
  monthlyColorTypes?: ('new' | 'modified' | 'removed' | 'unchanged')[];
}

export interface OOCProvNCVarianceRow {
  agency: 'VA' | 'DOD';
  historical12MVolume: number;
  nceVolume: number;
  // Baseline nceVolume before any user edits, and the resulting color-coded
  // edit status — mirrors OOCForecastCell.previousValue/colorType so the
  // Prov NC grid gets the same new/modified/removed/unchanged treatment as
  // the normal MAS/NC forecast grids.
  originalNceVolume?: number | null;
  colorType?: 'new' | 'modified' | 'removed' | 'unchanged';
}

// Per-(FacilityCOT, PackSize) volume row — same shape and source
// (AnnualMarketSales) as ProvisionalForecastV2's `MarketDataRow`. Drives
// the pack-size checkbox recompute for both ProvMAS and ProvNC.
export interface OOCMarketDataRow {
  facility_cot: string;
  packsize: string; // decimal as string
  annual_market: string; // decimal as string
  group?: string | null;
}

export interface OOCProvForecastData {
  ndc11: string;
  contractType: 'MAS' | 'NC';
  facilityRows: OOCProvFacilityRow[];
  varianceRows: OOCProvNCVarianceRow[];
  marketData: OOCMarketDataRow[];
  selectedPackSizes: SelectedPackSize[];
}

// ─── Supplier Notification ───────────────────────────────────
export interface OOCSupplierNotification {
  ndc11: string;
  supplier: string;
  bulkChangePercent: number;
  feedbackDeadline: string;
  notified: boolean;
}

// ─── Business Rule Validation ────────────────────────────────
export interface OOCValidationResult {
  isValid: boolean;
  warnings: OOCValidationWarning[];
}

export interface OOCValidationWarning {
  type: 'threshold' | 'blackout' | 'sla';
  message: string;
  ndc11?: string;
  canOverride: boolean;
}
